import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSettingsStore } from '@/store/settingsStore';

/**
 * Hook for managing query invalidation with proper cache management
 * Ensures fresh data is fetched from the server when refresh is triggered
 */
export function useQueryInvalidation() {
  const queryClient = useQueryClient();
  const { selectedOrganization, selectedProject } = useSettingsStore();

  /**
   * Invalidate queries with force refetch to ensure fresh data from server
   */
  const invalidateWithRefetch = useCallback(
    (queryKey: string[]) => {
      queryClient.invalidateQueries({
        queryKey,
        exact: false,
        refetchType: 'active',
      });
    },
    [queryClient]
  );

  /**
   * Invalidate dashboard queries for current context
   */
  const invalidateDashboard = useCallback(() => {
    console.log('🔄 Invalidating dashboard queries...');
    
    queryClient.invalidateQueries({
      queryKey: ['dashboard'],
      exact: false,
      refetchType: 'active',
    });
    
    console.log('✅ Dashboard queries invalidated and refetched');
  }, [queryClient]);

  /**
   * Invalidate face recognition queries for current context
   */
  const invalidateFaceRecognition = useCallback(() => {
    console.log('🔄 Invalidating face recognition queries...');
    
    queryClient.invalidateQueries({
      queryKey: ['face-recognition'],
      exact: false,
      refetchType: 'active',
    });
    
    console.log('✅ Face recognition queries invalidated and refetched');
  }, [queryClient]);

  /**
   * Invalidate identity validation queries
   */
  const invalidateIdentityValidation = useCallback(() => {
    console.log('🔄 Invalidating identity validation queries...');
    
    queryClient.invalidateQueries({
      queryKey: ['identity-validations'],
      exact: false,
      refetchType: 'active',
    });
    
    console.log('✅ Identity validation queries invalidated and refetched');
  }, [queryClient]);

  /**
   * Invalidate all queries for current organization and project
   */
  const invalidateCurrentContext = useCallback(() => {
    if (selectedOrganization && selectedProject) {
      // Remove all queries for current context from cache
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.includes(selectedOrganization.id) &&
            queryKey.includes(selectedProject.id)
          );
        },
      });
      
      // Then invalidate to trigger fresh fetches
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.includes(selectedOrganization.id) &&
            queryKey.includes(selectedProject.id)
          );
        },
        refetchType: 'active',
      });
    }
  }, [queryClient, selectedOrganization, selectedProject]);

  return {
    invalidateDashboard,
    invalidateFaceRecognition,
    invalidateIdentityValidation,
    invalidateCurrentContext,
    invalidateWithRefetch,
  };
}